import { User } from "firebase/auth";
import { useAuth } from "./useAuth";

// Provider-id prefixes of identity providers configured by an admin in
// Identity Platform (SAML / OIDC). Social providers (google.com, github.com,
// …) are deliberately excluded: they're not enterprise SSO, and the ones that
// do verify emails already set Firebase's own emailVerified flag.
const ENTERPRISE_SSO_PREFIXES = ["saml.", "oidc."];

const isEnterpriseSsoUser = (user: User) =>
  user.providerData.some(({ providerId }) =>
    ENTERPRISE_SSO_PREFIXES.some((prefix) => providerId.startsWith(prefix)),
  );

// An enterprise identity provider already vouches for the signed-in email,
// but Firebase's emailVerified flag is often false by default for SAML/OIDC
// accounts — so it's only trusted as a fallback for those providers.
export const isEmailVerified = (user: User) =>
  user.emailVerified || isEnterpriseSsoUser(user);

// An enterprise SSO sign-in always resolves to a single company, so there's
// no "start a new company" choice to offer.
// `user.providerData` is populated synchronously as part of the Firebase
// User object (no separate token fetch), so this resolves to a real boolean
// as soon as `user` itself does — deliberately not sourced from ID token
// claims, whose async refresh can fail independently and would otherwise
// leave callers waiting on `undefined` forever.
export const useIsSsoUser = (): boolean | undefined => {
  const { user } = useAuth();
  if (!user) return undefined;
  return isEnterpriseSsoUser(user);
};
